import { useMemo } from 'react';
import { useGetLotSizing } from '@/services/apis/queries/lot-sizing/use-get-lot-sizing';
import { useLotSizingPurchaseOrderDraft } from '../lot-sizing-purchase-order-draft-context';

type DraftPreviewState = {
  missingVolumes: Map<string, string | number>;
  supplierAmounts: Map<string, number>;
  totalAmountDelta: number;
  volumeDelta: number;
};

function applyAmountDelta(
  supplierAmounts: Map<string, number>,
  supplierId: number | string,
  delta: number,
) {
  const key = String(supplierId);
  supplierAmounts.set(key, (supplierAmounts.get(key) ?? 0) + delta);
}

function applyDraftLineChange(
  state: DraftPreviewState,
  params: {
    checked: boolean;
    originalChecked?: boolean;
    quantity: number;
    originalQuantity?: number;
    unitPrice: number;
    unitVolume: number | null;
    supplierId: number | string;
    productId: number | string;
  },
) {
  const originalQuantity = params.originalChecked
    ? (params.originalQuantity ?? 0)
    : 0;
  const nextQuantity = params.checked ? params.quantity : 0;
  const quantityDelta = nextQuantity - originalQuantity;

  if (quantityDelta === 0) {
    return;
  }

  const amountDelta = quantityDelta * params.unitPrice;
  applyAmountDelta(state.supplierAmounts, params.supplierId, amountDelta);
  state.totalAmountDelta += amountDelta;

  if (params.unitVolume === null) {
    if (nextQuantity > 0) {
      state.missingVolumes.set(String(params.productId), params.productId);
      return;
    }

    state.missingVolumes.delete(String(params.productId));
    return;
  }

  state.volumeDelta += quantityDelta * params.unitVolume;
}

export function useLotSizingDraftPreview(lotSizingId: string) {
  const { data: lotSizing } = useGetLotSizing(lotSizingId);
  const { draftAddedLines, draftChanges } = useLotSizingPurchaseOrderDraft();

  return useMemo(() => {
    const missingVolumes = new Map<string, string | number>(
      lotSizing.missingProductVolumes.map((id) => [String(id), id]),
    );
    const supplierAmounts = new Map<string, number>(
      lotSizing.suppliers.map((supplier) => [
        String(supplier.id),
        supplier.currentLotSizingAmount,
      ]),
    );
    const draftPreviewState: DraftPreviewState = {
      missingVolumes,
      supplierAmounts,
      totalAmountDelta: 0,
      volumeDelta: 0,
    };

    for (const change of draftChanges) {
      applyDraftLineChange(draftPreviewState, {
        checked: change.checked,
        originalChecked: change.originalChecked,
        originalQuantity: change.originalQuantity,
        productId: change.productId,
        quantity: change.quantity,
        supplierId: change.supplierId,
        unitPrice: change.unitPrice,
        unitVolume: change.unitVolume,
      });
    }

    for (const line of draftAddedLines) {
      applyDraftLineChange(draftPreviewState, {
        checked: line.checked,
        productId: line.product.id,
        quantity: line.quantity,
        supplierId: line.supplierId,
        unitPrice: line.unitPrice,
        unitVolume: line.unitVolume,
      });
    }

    const totalAmount = Math.max(
      0,
      lotSizing.totalAmount + draftPreviewState.totalAmountDelta,
    );

    return {
      currentVolume: Math.max(
        0,
        (lotSizing.volume ?? 0) + draftPreviewState.volumeDelta,
      ),
      missingProductVolumes: Array.from(missingVolumes.values()),
      noProductSelected: totalAmount === 0,
      supplierAmounts: new Map(
        Array.from(supplierAmounts.entries()).map(([supplierId, amount]) => [
          supplierId,
          Math.max(0, amount),
        ]),
      ),
      totalAmount,
    };
  }, [draftAddedLines, draftChanges, lotSizing]);
}
